import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, IconButton,
  Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Chip, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Public as GlobalIcon,
  Business as CompanyIcon,
  Home as BuildingIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

// Default values for new templates
const DEFAULT_TEMPLATE = {
  name: '',
  description: '',
  building_type: '',
  zip_code: '',
  source: 'uploaded',
  is_global: false
};

// Simple climate zone lookup based on US zip code prefix
// Uses ASHRAE climate zones (1-8) with moisture indicators (A=moist, B=dry, C=marine)
const getClimateZoneFromZip = (zipCode) => {
  if (!zipCode || zipCode.length < 3) return null;
  const prefix = parseInt(zipCode.slice(0, 3), 10);

  // Very hot (Zone 1-2)
  if ((prefix >= 850 && prefix <= 865) || // Southern AZ
      (prefix >= 700 && prefix <= 714) || // Southern LA
      (prefix >= 780 && prefix <= 799) || // Southern TX
      (prefix >= 919 && prefix <= 928)) { // Southern CA
    return '2B';
  }

  // Hot-humid (Zone 2A) - Southeast
  if ((prefix >= 300 && prefix <= 399) || // GA, FL
      (prefix >= 320 && prefix <= 349) || // FL
      (prefix >= 700 && prefix <= 729) || // LA, MS
      (prefix >= 750 && prefix <= 779)) { // TX (Houston area)
    return '2A';
  }

  // Warm (Zone 3) - Mixed
  if ((prefix >= 270 && prefix <= 289) || // NC
      (prefix >= 290 && prefix <= 299) || // SC
      (prefix >= 350 && prefix <= 369) || // AL
      (prefix >= 370 && prefix <= 385) || // TN
      (prefix >= 716 && prefix <= 749) || // TX (Dallas area)
      (prefix >= 730 && prefix <= 749)) { // OK
    return '3A';
  }

  // California coast (Zone 3C)
  if ((prefix >= 900 && prefix <= 918) || // LA area
      (prefix >= 940 && prefix <= 951)) { // SF Bay area
    return '3C';
  }

  // Mixed (Zone 4)
  if ((prefix >= 200 && prefix <= 219) || // DC, VA
      (prefix >= 220 && prefix <= 246) || // VA, WV
      (prefix >= 247 && prefix <= 268) || // WV, NC
      (prefix >= 400 && prefix <= 427) || // KY
      (prefix >= 430 && prefix <= 458) || // OH
      (prefix >= 460 && prefix <= 479) || // IN
      (prefix >= 630 && prefix <= 658) || // MO
      (prefix >= 660 && prefix <= 679) || // KS
      (prefix >= 680 && prefix <= 693)) { // NE
    return '4A';
  }

  // Cold (Zone 5)
  if ((prefix >= 100 && prefix <= 149) || // NY
      (prefix >= 150 && prefix <= 196) || // PA, NJ
      (prefix >= 480 && prefix <= 499) || // MI
      (prefix >= 500 && prefix <= 528) || // IA
      (prefix >= 530 && prefix <= 549) || // WI
      (prefix >= 550 && prefix <= 567) || // MN
      (prefix >= 600 && prefix <= 629) || // IL
      (prefix >= 800 && prefix <= 816) || // CO
      (prefix >= 820 && prefix <= 831)) { // WY
    return '5A';
  }

  // Very cold (Zone 6)
  if ((prefix >= 10 && prefix <= 59) || // MA, RI, NH, VT
      (prefix >= 60 && prefix <= 69) || // CT
      (prefix >= 570 && prefix <= 577) || // SD
      (prefix >= 580 && prefix <= 588) || // ND
      (prefix >= 590 && prefix <= 599) || // MT
      (prefix >= 832 && prefix <= 838)) { // ID
    return '6A';
  }

  // Subarctic (Zone 7)
  if ((prefix >= 995 && prefix <= 999)) { // AK
    return '7';
  }

  // Default to Zone 4A for unknown
  return '4A';
};

const SOURCE_OPTIONS = [
  { value: 'uploaded', label: 'Upload CSV Data', icon: UploadIcon },
];

// CSV template with both hourly and 15-minute examples
const CSV_TEMPLATE_HOURLY = `timestamp,kw
# HOURLY FORMAT (8760 rows for a full year)
# One row per hour
2024-01-01 00:00,100
2024-01-01 01:00,95
2024-01-01 02:00,90
2024-01-01 03:00,88
# ... continue for all 8760 hours of the year`;

const CSV_TEMPLATE_15MIN = `timestamp,kw
# 15-MINUTE FORMAT (35040 rows for a full year)
# Four rows per hour
2024-01-01 00:00,100
2024-01-01 00:15,98
2024-01-01 00:30,96
2024-01-01 00:45,95
2024-01-01 01:00,95
# ... continue for all 35040 intervals of the year`;

const downloadCsvTemplate = (interval = 'hourly') => {
  const content = interval === '15min' ? CSV_TEMPLATE_15MIN : CSV_TEMPLATE_HOURLY;
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `load_profile_template_${interval}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Parse CSV containing hourly (8760) or 15-min (35040) kW load values
 * Supports formats:
 *   timestamp,kw (with header)
 *   2024-01-01 00:00,100
 *   2024-01-01,00:00,100
 *   100 (just values)
 */
function parseLoadCsv(text) {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .filter((l) => l.trim() && !l.trim().startsWith('#'));

  let year = 2024;
  const values = [];

  for (const line of lines) {
    // Split by comma first (standard CSV)
    const commaParts = line.split(',').map(p => p.trim());

    // Skip header row
    if (commaParts[0].toLowerCase() === 'timestamp' ||
        commaParts[0].toLowerCase() === 'date' ||
        commaParts[0].toLowerCase() === 'datetime') {
      continue;
    }

    // Find the numeric value - it's typically the last column
    let numValue = null;
    for (let i = commaParts.length - 1; i >= 0; i--) {
      const num = parseFloat(commaParts[i]);
      if (!isNaN(num) && commaParts[i].match(/^-?\d+\.?\d*$/)) {
        numValue = num;
        break;
      }
    }

    if (numValue !== null) {
      values.push(numValue);
    }

    // Try to extract year from first column (timestamp)
    const dateStr = commaParts[0];
    const dt = Date.parse(dateStr);
    if (!isNaN(dt)) {
      year = new Date(dt).getFullYear();
    }
  }

  // 15-minute data - convert to hourly by averaging
  // Standard year: 35040, Leap year: 35136
  if (values.length === 35040 || values.length === 35136) {
    const hourly = [];
    // Only process first 35040 values (8760 hours) to normalize leap years
    const count = Math.min(values.length, 35040);
    for (let i = 0; i < count; i += 4) {
      hourly.push(
        (values[i] + values[i + 1] + values[i + 2] + values[i + 3]) / 4,
      );
    }
    return { year, loads: hourly, originalInterval: '15min', originalCount: values.length };
  }

  // Hourly data - Standard year: 8760, Leap year: 8784
  if (values.length === 8760 || values.length === 8784) {
    // Normalize to 8760 by trimming leap day if present
    const loads = values.slice(0, 8760);
    return { year, loads, originalInterval: 'hourly', originalCount: values.length };
  }

  throw new Error(`Expected 8760/8784 hourly or 35040/35136 15-min values, got ${values.length}`);
}

/**
 * Summarize an 8760 load array
 */
function summarizeLoads(arr) {
  if (!Array.isArray(arr) || arr.length === 0) {
    return { total: 0, peak: 0, loadFactor: 0 };
  }
  const total = arr.reduce((a, b) => a + b, 0);
  const peak = Math.max(...arr);
  const loadFactor = peak ? total / (peak * 8760) : 0;
  return { total, peak, loadFactor };
}

const LoadProfilesPage = () => {
  const { currentUser, userProfile } = useAuth();
  const { isRetro } = useThemeMode();
  const [templates, setTemplates] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_TEMPLATE });
  const [saving, setSaving] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [parsedLoads, setParsedLoads] = useState(null);
  const [parseInfo, setParseInfo] = useState(null);

  const isSuperAdmin = userProfile?.is_super_admin;

  // Calculate load summary when loads are parsed
  const loadSummary = useMemo(() => {
    if (!parsedLoads || parsedLoads.length === 0) return null;
    return summarizeLoads(parsedLoads);
  }, [parsedLoads]);

  // Extract peak day (24 hours containing the peak load)
  const peakDayData = useMemo(() => {
    if (!parsedLoads || parsedLoads.length < 24) return null;

    // Find the hour with peak load
    let peakIdx = 0;
    let peakVal = parsedLoads[0];
    for (let i = 1; i < parsedLoads.length; i++) {
      if (parsedLoads[i] > peakVal) {
        peakVal = parsedLoads[i];
        peakIdx = i;
      }
    }

    // Get the day number (0-indexed) and extract that day's 24 hours
    const dayNum = Math.floor(peakIdx / 24);
    const dayStartIdx = dayNum * 24;
    const dayEndIdx = Math.min(dayStartIdx + 24, parsedLoads.length);

    // Build chart data with hour labels
    const chartData = [];
    for (let i = dayStartIdx; i < dayEndIdx; i++) {
      chartData.push({
        hour: i - dayStartIdx,
        load: parsedLoads[i]
      });
    }

    // Calculate which month/day this is
    const dayOfYear = dayNum + 1;
    const date = new Date(2024, 0, dayOfYear); // Use 2024 as reference year
    const monthName = date.toLocaleString('default', { month: 'short' });
    const dayOfMonth = date.getDate();

    return {
      data: chartData,
      dayLabel: `${monthName} ${dayOfMonth}`,
      peakHour: peakIdx - dayStartIdx
    };
  }, [parsedLoads]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadError('Please upload a CSV file');
      setParsedLoads(null);
      setParseInfo(null);
      return;
    }

    setUploadedFile(file);
    setUploadError(null);

    // Parse the file to show preview
    try {
      const text = await file.text();
      const result = parseLoadCsv(text);
      setParsedLoads(result.loads);
      setParseInfo({
        year: result.year,
        interval: result.originalInterval,
        count: result.originalCount
      });
    } catch (err) {
      setUploadError(err.message);
      setParsedLoads(null);
      setParseInfo(null);
    }
  };

  // Retro styling for form inputs
  const retroInputSx = isRetro ? {
    '& .MuiOutlinedInput-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.85rem',
      bgcolor: 'rgba(0, 0, 0, 0.3)',
      '& fieldset': {
        borderColor: 'rgba(63, 106, 100, 0.4)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(178, 140, 80, 0.6)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#b28c50',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.8rem',
      color: 'rgba(200, 200, 200, 0.7)',
      '&.Mui-focused': {
        color: '#b28c50',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#c8c8c8',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.7rem',
      color: 'rgba(200, 200, 200, 0.5)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(178, 140, 80, 0.7)',
    },
  } : {};

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();

      // Fetch building types and templates in parallel
      const [typesRes, templatesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/site_dna/v1/load-profiles/building-types`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/site_dna/v1/load-profiles/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!typesRes.ok || !templatesRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [typesData, templatesData] = await Promise.all([
        typesRes.json(),
        templatesRes.json()
      ]);

      setBuildingTypes(typesData.building_types || []);
      setTemplates(templatesData.templates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, fetchData]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ ...template });
    } else {
      setEditingTemplate(null);
      setFormData({ ...DEFAULT_TEMPLATE });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setFormData({ ...DEFAULT_TEMPLATE });
    setUploadedFile(null);
    setUploadError(null);
    setParsedLoads(null);
    setParseInfo(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const url = editingTemplate
        ? `/api/site_dna/v1/load-profiles/templates/${editingTemplate.id}`
        : '/api/site_dna/v1/load-profiles/templates';
      const method = editingTemplate ? 'PUT' : 'POST';

      // Derive climate zone from zip code
      const climateZone = formData.zip_code ? getClimateZoneFromZip(formData.zip_code) : null;

      // Build payload with calculated values from parsed load data
      const payload = {
        ...formData,
        // Use calculated values from parsed load data if available
        annual_kwh: loadSummary?.total || null,
        peak_kw: loadSummary?.peak || null,
        load_factor: loadSummary?.loadFactor || null,
        climate_zone: climateZone,
        // Include the 8760 load array if we have parsed data
        load_data: parsedLoads || null,
      };

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save template');
      }

      await fetchData();
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-profiles/templates/${template.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete template');
      }

      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getBuildingTypeName = (typeId) => {
    const type = buildingTypes.find(t => t.id === typeId);
    return type?.name || typeId || '-';
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '-';
    return parseFloat(num).toLocaleString();
  };

  return (
    <AdminLayout title="Load Profiles">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Manage building load profile templates for different building types and climate zones.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Profile
          </Button>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info">
          No load profile templates found. Create your first template to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Building Type</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Annual kWh</TableCell>
                <TableCell align="right">Peak kW</TableCell>
                <TableCell>Climate Zone</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => {
                const canEdit = isSuperAdmin || (!template.is_global && template.company_id === userProfile?.company_id);
                return (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BuildingIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{template.name}</Typography>
                          {template.description && (
                            <Typography variant="caption" color="text.secondary">{template.description}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getBuildingTypeName(template.building_type)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={template.is_global ? <GlobalIcon /> : <CompanyIcon />}
                        label={template.is_global ? 'Global' : 'Company'}
                        size="small"
                        sx={{
                          bgcolor: alpha(template.is_global ? vortxColors.teal : vortxColors.gold, 0.2),
                          color: template.is_global ? vortxColors.teal : vortxColors.gold
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatNumber(template.annual_kwh)}</TableCell>
                    <TableCell align="right">{formatNumber(template.peak_kw)}</TableCell>
                    <TableCell>{template.climate_zone || '-'}</TableCell>
                    <TableCell align="center">
                      {canEdit && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(template)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(template)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isRetro ? {
            bgcolor: '#1a1a1a',
            borderRadius: 0,
            border: '1px solid rgba(63, 106, 100, 0.4)',
            boxShadow: '0 0 30px rgba(0, 0, 0, 0.8)',
          } : {}
        }}
      >
        <DialogTitle sx={isRetro ? {
          fontFamily: vortxTypography.heading,
          fontSize: '1.1rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#b28c50',
          borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
          pb: 2,
        } : {}}>
          {editingTemplate ? 'Edit Load Profile' : 'New Load Profile Template'}
        </DialogTitle>
        <DialogContent sx={{ pt: 6, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Row 1: Profile Name + Building Type */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Profile Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  sx={retroInputSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth sx={retroInputSx}>
                  <InputLabel>Building Type</InputLabel>
                  <Select
                    value={formData.building_type || ''}
                    label="Building Type"
                    onChange={(e) => handleFormChange('building_type', e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: isRetro ? {
                          bgcolor: '#1a1a1a',
                          border: '1px solid rgba(63, 106, 100, 0.4)',
                          '& .MuiMenuItem-root': {
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                            color: '#c8c8c8',
                            '&:hover': {
                              bgcolor: 'rgba(178, 140, 80, 0.15)',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(178, 140, 80, 0.25)',
                            },
                          },
                        } : {}
                      }
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {buildingTypes.map(type => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Row 2: Description */}
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              sx={retroInputSx}
            />

            {/* Row 3: Zip Code + Climate Zone */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: 180 }}>
                <TextField
                  label="Zip Code"
                  fullWidth
                  value={formData.zip_code || ''}
                  onChange={(e) => handleFormChange('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="e.g., 94102"
                  inputProps={{ maxLength: 5 }}
                  helperText="For climate zone lookup"
                  sx={retroInputSx}
                />
              </Box>
              {formData.zip_code && formData.zip_code.length >= 3 && (
                <Box sx={{
                  pt: 1,
                  pl: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}>
                  <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.mono : undefined,
                    fontSize: '0.7rem',
                    color: isRetro ? '#888' : 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Climate Zone
                  </Typography>
                  <Typography sx={{
                    fontFamily: isRetro ? vortxTypography.heading : undefined,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: isRetro ? '#5fd4c4' : 'secondary.main',
                  }}>
                    {getClimateZoneFromZip(formData.zip_code) || '-'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Data Source Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={isRetro ? {
                  fontFamily: vortxTypography.heading,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#5fd4c4',
                  borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                  pb: 1,
                  mb: 2,
                } : { color: 'text.secondary', mb: 2 }}
              >
                Load Data Source
              </Typography>

              {/* Source Selection */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {SOURCE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const isSelected = formData.source === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      variant={isSelected ? 'contained' : 'outlined'}
                      startIcon={<Icon />}
                      onClick={() => handleFormChange('source', opt.value)}
                      sx={isRetro ? {
                        flex: 1,
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderRadius: 0,
                        py: 1.5,
                        ...(isSelected ? {
                          bgcolor: '#b28c50',
                          color: '#1a1a1a',
                          '&:hover': { bgcolor: '#c9a05e' },
                        } : {
                          borderColor: 'rgba(63, 106, 100, 0.4)',
                          color: '#888',
                          '&:hover': {
                            borderColor: '#b28c50',
                            bgcolor: 'rgba(178, 140, 80, 0.1)',
                          },
                        }),
                      } : {
                        flex: 1,
                        textTransform: 'none',
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Box>

              {/* Conditional Content Based on Source */}
              {formData.source === 'uploaded' && (
                <Box sx={{
                  border: isRetro ? '1px dashed rgba(63, 106, 100, 0.4)' : '1px dashed rgba(0,0,0,0.2)',
                  borderRadius: isRetro ? 0 : 1,
                  p: 3,
                  bgcolor: isRetro ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0,0,0,0.02)',
                }}>
                  {/* Upload Area */}
                  <Box sx={{ textAlign: 'center', mb: loadSummary ? 3 : 0 }}>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        sx={isRetro ? {
                          fontFamily: vortxTypography.mono,
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderRadius: 0,
                          borderColor: '#5fd4c4',
                          color: '#5fd4c4',
                          mb: 2,
                          '&:hover': {
                            borderColor: '#5fd4c4',
                            bgcolor: 'rgba(95, 212, 196, 0.1)',
                          },
                        } : { mb: 2 }}
                      >
                        {uploadedFile ? 'Change File' : 'Upload CSV'}
                      </Button>
                    </label>

                    {uploadedFile && parseInfo && (
                      <Typography sx={{
                        fontFamily: isRetro ? vortxTypography.mono : undefined,
                        fontSize: '0.85rem',
                        color: isRetro ? '#5fd4c4' : 'success.main',
                        mb: 1,
                      }}>
                        ✓ {uploadedFile.name} ({parseInfo.interval === '15min' ? '15-minute' : 'Hourly'} data, {parseInfo.count.toLocaleString()} values)
                      </Typography>
                    )}

                    {uploadError && (
                      <Typography sx={{
                        fontFamily: isRetro ? vortxTypography.mono : undefined,
                        fontSize: '0.85rem',
                        color: '#ef4444',
                        mb: 1,
                      }}>
                        {uploadError}
                      </Typography>
                    )}

                    {!loadSummary && (
                      <>
                        <Typography variant="body2" sx={{
                          color: isRetro ? '#888' : 'text.secondary',
                          fontFamily: isRetro ? vortxTypography.mono : undefined,
                          fontSize: isRetro ? '0.75rem' : undefined,
                          mb: 2,
                        }}>
                          Supports both hourly (8760 values) and 15-minute (35040 values) formats
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadCsvTemplate('hourly')}
                            sx={isRetro ? {
                              fontFamily: vortxTypography.mono,
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              color: '#b28c50',
                              '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)' },
                            } : { textTransform: 'none' }}
                          >
                            Hourly Template
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadCsvTemplate('15min')}
                            sx={isRetro ? {
                              fontFamily: vortxTypography.mono,
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              color: '#b28c50',
                              '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)' },
                            } : { textTransform: 'none' }}
                          >
                            15-Min Template
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Load Profile Preview */}
                  {loadSummary && (
                    <Box sx={{
                      borderTop: isRetro ? '1px solid rgba(63, 106, 100, 0.3)' : '1px solid rgba(0,0,0,0.1)',
                      pt: 3,
                    }}>
                      <Typography
                        variant="subtitle2"
                        sx={isRetro ? {
                          fontFamily: vortxTypography.heading,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          color: '#5fd4c4',
                          mb: 2,
                        } : { color: 'text.secondary', mb: 2, fontWeight: 600 }}
                      >
                        Load Profile Preview
                      </Typography>

                      {/* Stats Row */}
                      <Box sx={{
                        display: 'flex',
                        gap: 3,
                        mb: 3,
                        flexWrap: 'wrap',
                      }}>
                        <Box>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                            fontSize: '0.7rem',
                            color: isRetro ? '#888' : 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}>
                            Total kWh
                          </Typography>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.heading : undefined,
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: isRetro ? '#b28c50' : 'primary.main',
                          }}>
                            {(loadSummary.total / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} MWh
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                            fontSize: '0.7rem',
                            color: isRetro ? '#888' : 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}>
                            Peak Load
                          </Typography>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.heading : undefined,
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: isRetro ? '#5fd4c4' : 'secondary.main',
                          }}>
                            {loadSummary.peak.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                            fontSize: '0.7rem',
                            color: isRetro ? '#888' : 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}>
                            Load Factor
                          </Typography>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.heading : undefined,
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: isRetro ? '#c8c8c8' : 'text.primary',
                          }}>
                            {(loadSummary.loadFactor * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>

                      {/* Peak Day Chart */}
                      {peakDayData && (
                        <>
                          <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                            fontSize: '0.75rem',
                            color: isRetro ? '#888' : 'text.secondary',
                            mb: 1,
                          }}>
                            Peak Day: {peakDayData.dayLabel}
                          </Typography>
                          <Box sx={{
                            height: 180,
                            bgcolor: isRetro ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0,0,0,0.02)',
                            borderRadius: isRetro ? 0 : 1,
                            border: isRetro ? '1px solid rgba(63, 106, 100, 0.2)' : 'none',
                            p: 1,
                          }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={peakDayData.data}>
                                <defs>
                                  <linearGradient id="peakDayGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={isRetro ? '#5fd4c4' : vortxColors.teal} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={isRetro ? '#5fd4c4' : vortxColors.teal} stopOpacity={0.1} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isRetro ? 'rgba(255,255,255,0.1)' : '#eee'} />
                                <XAxis
                                  dataKey="hour"
                                  tickFormatter={(h) => `${h}:00`}
                                  stroke={isRetro ? '#888' : '#666'}
                                  style={{ fontSize: 10 }}
                                  interval={2}
                                />
                                <YAxis
                                  stroke={isRetro ? '#888' : '#666'}
                                  tickFormatter={(val) => `${Math.round(val)}`}
                                  style={{ fontSize: 10 }}
                                  width={45}
                                  label={{
                                    value: 'kW',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { fontSize: 10, fill: isRetro ? '#888' : '#666' }
                                  }}
                                />
                                <RechartsTooltip
                                  labelFormatter={(h) => `Hour ${h}:00`}
                                  formatter={(val) => [`${Math.round(val)} kW`, 'Load']}
                                  contentStyle={{
                                    borderRadius: isRetro ? 0 : 8,
                                    border: isRetro ? '1px solid rgba(63, 106, 100, 0.4)' : 'none',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                    backgroundColor: isRetro ? '#1a1a1a' : '#fff',
                                    color: isRetro ? '#c8c8c8' : '#333',
                                    fontFamily: isRetro ? vortxTypography.mono : undefined,
                                    fontSize: '0.75rem',
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="load"
                                  stroke={isRetro ? '#5fd4c4' : vortxColors.teal}
                                  strokeWidth={2}
                                  fillOpacity={1}
                                  fill="url(#peakDayGradient)"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}

            </Box>

            {/* Global Toggle (Super Admin Only) */}
            {isSuperAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_global}
                    onChange={(e) => handleFormChange('is_global', e.target.checked)}
                    sx={isRetro ? {
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#b28c50',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#b28c50',
                      },
                    } : {}}
                  />
                }
                label="Make this a global template (available to all companies)"
                sx={isRetro ? {
                  '& .MuiFormControlLabel-label': {
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: '#c8c8c8',
                  },
                } : {}}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={isRetro ? {
          borderTop: '1px solid rgba(63, 106, 100, 0.3)',
          p: 2,
        } : {}}>
          <Button
            onClick={handleCloseDialog}
            sx={isRetro ? {
              fontFamily: vortxTypography.mono,
              fontSize: '0.8rem',
              color: '#c8c8c8',
              '&:hover': {
                bgcolor: 'rgba(200, 200, 200, 0.1)',
              },
            } : {}}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving ||
              !formData.name ||
              // For new templates with uploaded source, require a file
              // For editing existing templates, allow saving without new upload
              (formData.source === 'uploaded' && !uploadedFile && !editingTemplate)
            }
            sx={isRetro ? {
              fontFamily: vortxTypography.mono,
              fontSize: '0.8rem',
              fontWeight: 600,
              bgcolor: '#b28c50',
              color: '#1a1a1a',
              borderRadius: 0,
              '&:hover': {
                bgcolor: '#c9a05e',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(178, 140, 80, 0.3)',
                color: 'rgba(26, 26, 26, 0.5)',
              },
            } : {}}
          >
            {saving ? <CircularProgress size={20} /> : (editingTemplate ? 'Save Changes' : 'Create Template')}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default LoadProfilesPage;
